import { useReveal } from "@/lib/useReveal";
import { Users, Stethoscope, Building2, ArrowUpRight } from "lucide-react";

const audiences = [
  {
    icon: Users,
    title: "Patients & Healthcare Users",
    body: "Empowering everyday health journeys with clarity, privacy, and access to validated care intelligence.",
    tag: "Consumer",
  },
  {
    icon: Stethoscope,
    title: "Healthcare Professionals",
    body: "Tools that respect the clinician’s time, structured workflows, integrated data, and decision-grade precision.",
    tag: "Practitioner",
    featured: true,
  },
  {
    icon: Building2,
    title: "Clinics & Hospitals",
    body: "An infrastructure layer for modern health systems, interoperable, compliant, and quietly powerful.",
    tag: "Enterprise",
  },
];

export default function WhoWeServe() {
  const ref = useReveal();
  return (
    <section
      id="serve"
      data-testid="serve-section"
      ref={ref}
      className="relative overflow-hidden bg-[#F8F9FA] py-28 md:py-40"
    >
      {/* Subtle abstract video background */}
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <video
          data-testid="serve-video"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src={`${process.env.PUBLIC_URL}/videos/abstract.mp4`}
            type="video/mp4"
          />
        </video>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#5F6368] reveal">
          <span className="inline-block h-px w-10 bg-[#D62828]" />
          Who We Serve
        </div>

        <div className="mt-8 grid grid-cols-12 gap-8 items-end">
          <h2 className="col-span-12 lg:col-span-8 font-display text-[clamp(2.25rem,4.6vw,4.5rem)] leading-[0.96] tracking-[-0.035em] text-[#111111] reveal">
            Designed for the people who{" "}
            <span className="font-serif-italic italic text-[#D62828]">
              shape
            </span>{" "}
            healthcare every day.
          </h2>
          <p className="col-span-12 lg:col-span-4 text-[15px] leading-[1.7] text-[#5F6368] reveal">
            From the patient navigating a diagnosis to the hospital running a
            ward, every Blude TechX product is built for the realities of the
            people who use it.
          </p>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          {audiences.map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                data-testid={`audience-${i}`}
                className={`reveal group relative overflow-hidden rounded-3xl p-10 md:p-12 transition-all duration-700 hover:-translate-y-1 ${
                  a.featured
                    ? "bg-[#0F1B2D] text-white md:row-span-2 md:col-span-1 md:translate-y-[-24px] shadow-[0_30px_80px_-30px_rgba(15,27,45,0.55)]"
                    : "bg-white border border-[#CED4DA] text-[#111111] hover:shadow-[0_24px_50px_-30px_rgba(15,27,45,0.25)]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.24em] ${
                      a.featured
                        ? "bg-white/10 text-white"
                        : "bg-[#F8F9FA] text-[#D62828] border border-[#CED4DA]"
                    }`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D62828]" />
                    {a.tag}
                  </span>
                  <span
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${
                      a.featured ? "bg-white/10" : "bg-[#F8F9FA]"
                    }`}
                  >
                    <Icon
                      size={16}
                      className={a.featured ? "text-white" : "text-[#D62828]"}
                    />
                  </span>
                </div>

                <h3
                  className={`mt-10 md:mt-16 font-display tracking-[-0.035em] leading-[0.95] ${
                    a.featured ? "text-white" : "text-[#111111]"
                  }`}
                  style={{ fontSize: "clamp(1.75rem, 3.2vw, 2.75rem)" }}
                >
                  {a.title}
                </h3>
                <p
                  className={`mt-5 text-[14px] leading-[1.7] max-w-md ${
                    a.featured ? "text-white/70" : "text-[#5F6368]"
                  }`}
                >
                  {a.body}
                </p>

                <div
                  className={`mt-10 flex items-center justify-between border-t pt-5 ${
                    a.featured ? "border-white/15" : "border-[#CED4DA]"
                  }`}
                >
                  <span
                    className={`text-[11px] uppercase tracking-[0.22em] ${
                      a.featured ? "text-white/60" : "text-[#5F6368]"
                    }`}
                  >
                    0{i + 1} / 03
                  </span>
                  <ArrowUpRight
                    size={16}
                    className={`transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 ${
                      a.featured ? "text-white" : "text-[#111111]"
                    }`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
